import React, { useCallback } from 'react';
import MenuItem from '@mui/material/MenuItem';
import BasePersonTimelineRecordLayout from 'domain/shared/timeline/person/PersonTimelineRecord/BasePersonTimelineRecordLayout';
import { useShowContext } from 'framework/show';
import { TimelineType } from 'domain/shared/timeline/layout/BaseTimelineRecordLayout/timelineLayoutHelper';
import { Collapsible, HtmlField } from 'framework/ui/field';
import { DATE_FORMAT, ReplaceUrlWithLink, useAsyncValue, useSubmit } from 'framework/utils';
import {
  personTimelineActionNameSx,
  personTimelineDateSx,
  personTimelineHeaderSx,
} from 'domain/shared/timeline/person/PersonTimelineRecord/personTimelineRecordSx';
import {
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  useProjectSecurityContext,
  useNewProjectUrl,
} from 'domain/shared/newProject';
import useProjectNotesService from './useProjectNotesService';
import { useProjectId } from 'domain/shared/project';
import { PROJECT_NOTE_SOURCE, PROJECT_NOTE_TIMELINE_ICONS, PROJECT_TIMELINE_MESSAGE_CONTEXT } from './constants';
import { useListContext } from 'framework/list';
import DeleteConfirmationModal from './shared/DeleteConfirmationModal';
import { ITimelineItem, NotePayload } from './types';
import { EmployeeLinkFieldWithNameLoading } from 'domain/shared/employee';
import { ShowDate } from 'framework/ui/ui-builder';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';
import { PinTimelineButton } from 'domain/shared/timeline';
import { Box } from '@mui/material';

const ProjectNoteTimelineRecordHeader = () => {
  const { data: entity } = useShowContext();

  return (
    <Box sx={personTimelineHeaderSx}>
      <Box sx={personTimelineActionNameSx}>
        <EmployeeLinkFieldWithNameLoading value={entity?.updatedAt ? entity?.updatedBy : entity?.createdBy} />
      </Box>
      <Box sx={personTimelineDateSx}>
        <ShowDate dateUTC={entity?.updatedAt ? entity?.updatedAt : entity?.createdAt} format={DATE_FORMAT.FULL} />
      </Box>
    </Box>
  );
};

const ProjectNoteTimelineRecordLayout = (props: Record<string, unknown>) => {
  const { isAccessGranted } = useProjectSecurityContext();
  const { data } = useShowContext<ITimelineItem<NotePayload>>();
  const entity = data.getData();
  const isPinned = data.getValueBySource('isPinned');

  const { getUserAlias } = useAuthenticatedUserService();
  const currentUserAlias = useAsyncValue(() => getUserAlias());

  const { deleteUserNote, pinNote } = useProjectNotesService();
  const { getProjectTimelineUrl } = useNewProjectUrl();
  const projectId = useProjectId();

  const basePathname = getProjectTimelineUrl(projectId);
  const { updateInList, deleteFromList } = useListContext();

  const canManage = isAccessGranted(ACCESS_INFO_SECTIONS.PROJECT_TIMELINE_ENTRY, ACCESS_INFO_ACTIONS.MANAGE);
  const canPartialManage = isAccessGranted(
    ACCESS_INFO_SECTIONS.PROJECT_TIMELINE_ENTRY,
    ACCESS_INFO_ACTIONS.PARTIAL_MANAGE,
  );
  const isAuthor = entity.createdBy === currentUserAlias;
  const isAccountStrategy = entity.type === 'ACCOUNT_STRATEGY_NOTE';
  const isManageable = (canManage || (canPartialManage && isAuthor)) && !isAccountStrategy;

  const [modalOpen, setModalOpen] = React.useState(false);
  const { onSubmit, isLoading } = useSubmit({
    submitMethod: async () => {
      await deleteUserNote(entity.id);
      return {};
    },
    afterSubmit: { execute: () => (deleteFromList ? deleteFromList(entity.timelineId) : null) },
  });

  const pinTimelineAction = useCallback(async () => {
    const nextPinnedState = !Boolean(isPinned);
    const response = await pinNote(entity?.timelineId, nextPinnedState);

    if (response) {
      updateInList?.(entity.timelineId, {
        id: entity.timelineId,
        timelineId: entity.timelineId,
        type: response.type ?? entity.type,
        createdAt: response.createdAt ?? entity.createdAt,
        updatedAt: response.updatedAt ?? entity.updatedAt,
        createdBy: response.createdBy ?? entity.createdBy,
        updatedBy: response.updatedBy ?? entity.updatedBy,
        isPinned: nextPinnedState,
        [PROJECT_NOTE_SOURCE]: {
          ...entity,
          ...response,
          timelineId: entity.timelineId,
          isPinned: nextPinnedState,
        },
      });
    }
  }, [entity, isPinned, pinNote, updateInList]);

  const buttons = [
    isManageable && (
      <MenuItem key="edit" onClick={props.onEdit as React.MouseEventHandler<HTMLLIElement>}>
        Edit note
      </MenuItem>
    ),
    isManageable && (
      <MenuItem key="delete" onClick={() => setModalOpen(true)}>
        Delete note
      </MenuItem>
    ),
  ];

  return (
    <>
      <DeleteConfirmationModal
        isOpen={modalOpen}
        isLoading={isLoading}
        message="Do you really want to delete this timeline note?"
        onConfirm={onSubmit}
        onCancel={() => setModalOpen(false)}
      />
      <BasePersonTimelineRecordLayout
        actions={[
          <PinTimelineButton
            key={'notePin'}
            sx={{ fontSize: 16, ...(isAccountStrategy && { color: 'disabled' }) }}
            disabled={isAccountStrategy}
            pinStatus={Boolean(isPinned)}
            pinMethod={pinTimelineAction}
          />,
        ]}
        actionName=""
        timelineType={TimelineType.NOTE}
        buttons={buttons}
        timelineIcons={PROJECT_NOTE_TIMELINE_ICONS}
        timelineHeader={ProjectNoteTimelineRecordHeader}
        artifactsSource="payload.attachmentPaths"
        noteTextSource="payload.text"
        basePathname={basePathname}
        fields={
          <Collapsible>
            <ReplaceUrlWithLink>
              <HtmlField source="payload.text" label="" />
            </ReplaceUrlWithLink>
          </Collapsible>
        }
        permissionToCheck={null}
        isCandidateService={false}
        isProjectService={true}
        timelineFooter={() => null}
        isShowButtons={() => true}
        {...props}
        messageContext={PROJECT_TIMELINE_MESSAGE_CONTEXT}
      />
    </>
  );
};

export default ProjectNoteTimelineRecordLayout;
